/**
 * Inline-store types for the speedtest carve. Lives at a leaf in the
 * dep graph (both `index.ts` and `engine.ts` import from here) so the
 * `TestState` enum doesn't form a cycle.
 *
 * `MetricStates` tracks per-phase lifecycle. `UnitInfo` is the
 * formatter's display contract. `LiveTestData` is the reactive snapshot
 * the engine pushes through onProgress. `PhaseCompletePayload` is the
 * boundary-fire shape submitted to the API.
 */

import type { Phase } from "@speedtest/types.ts";

/** Per-metric lifecycle state. */
export enum TestState {
    notStarted = 0,
    started = 1,
    active = 2,
    finished = 3,
}

/** Reactive per-phase lifecycle tracker. Keys are `PhaseId` (3-set: ping,
 *  download, upload). Jitter has no separate slot — it rides ping. */
export interface MetricStates {
    ping: TestState;
    download: TestState;
    upload: TestState;
    [key: string]: TestState;
}

/** Formatted display value for a metric. */
export interface UnitInfo {
    amount?: string;
    unit?: string;
    /**
     * Raw input value in the BASE unit (always Mbps for throughput, always
     * ms for latency). NEVER the post-scaling magnitude — the formatter's
     * scaled amount is encoded in `amount` (the string) for display, and
     * `rawAmount` is the unscaled signal hysteresis consumers
     * (useAnimatedNumber) feed back through the formatter on each frame.
     *
     * The field is named `rawAmount` (not `value`) because both
     * branches of `formatThroughput` (auto + lock-mbps) write the raw
     * Mbps here — a prior `value`-named version had one branch writing
     * the scaled amount, which caused the smoother + formatter to
     * re-ladder and drop the unit one rung below correct.
     */
    rawAmount?: number | null;
}

/** Live test data — the reactive snapshot of current test state. */
export interface LiveTestData {
    phase: Phase | "finished" | null;
    downloadSpeed: number; // Mbps
    uploadSpeed: number; // Mbps
    ping: number; // ms
    jitter: number; // ms
    downloadProgress: number; // 0–1
    uploadProgress: number; // 0–1
    pingProgress: number; // 0–1
}

/** Cumulative metric snapshot fired at each phase boundary. */
export interface PhaseCompletePayload {
    ping: number | null;
    jitter: number | null;
    download: number | null;
    upload: number | null;
}

/** Numeric-amount kind read by the meter renderer. */
export type SpeedtestStateAmountKind = "Amount" | "Progress";
